import type { TFunction } from 'i18next';
import { createEmptyStepDraft, moveTemplateStep } from '@/lib/treatmentCourseTemplatePayload';
import type {
  IntervalUnit,
  TreatmentCourseTemplateDraft,
  TreatmentCourseTemplateStepDraft,
} from '@/types/treatmentCourses';

export type TreatmentCourseEditorSelection =
  | { kind: 'general' }
  | { kind: 'step'; clientId: string };

export type TreatmentCourseEditorState = {
  draft: TreatmentCourseTemplateDraft;
  selected: TreatmentCourseEditorSelection;
};

export type TreatmentCourseStepPanelTab =
  | 'appointment'
  | 'practitioners'
  | 'resources'
  | 'questionnaires';

export function createTreatmentCourseEditorState(
  draft: TreatmentCourseTemplateDraft,
  selected: TreatmentCourseEditorSelection = { kind: 'general' },
): TreatmentCourseEditorState {
  return { draft, selected: resolveSelection(draft, selected) };
}

export function selectTreatmentCourseEditorItem(
  state: TreatmentCourseEditorState,
  selected: TreatmentCourseEditorSelection,
): TreatmentCourseEditorState {
  return { ...state, selected: resolveSelection(state.draft, selected) };
}

export function updateTreatmentCourseEditorDraft(
  state: TreatmentCourseEditorState,
  draft: TreatmentCourseTemplateDraft,
): TreatmentCourseEditorState {
  return {
    draft,
    selected: resolveSelection(draft, state.selected),
  };
}

export function addTreatmentCourseEditorStep(
  state: TreatmentCourseEditorState,
): TreatmentCourseEditorState {
  const step = createEmptyStepDraft(state.draft.steps.length === 0);
  const draft = { ...state.draft, steps: [...state.draft.steps, step] };
  return {
    draft,
    selected: { kind: 'step', clientId: step.clientId },
  };
}

export function removeTreatmentCourseEditorStep(
  state: TreatmentCourseEditorState,
  clientId: string,
): TreatmentCourseEditorState {
  const index = state.draft.steps.findIndex((step) => step.clientId === clientId);
  if (index < 0 || state.draft.steps.length <= 1) return state;
  const remaining = state.draft.steps.filter((step) => step.clientId !== clientId);
  const steps = remaining.map((step, stepIndex) =>
    stepIndex === 0
      ? { ...step, interval_value: 0, interval_tolerance_value: 0, interval_unit: 'days' as const }
      : step,
  );
  const nextSelectedIndex = Math.min(index, steps.length - 1);
  const nextStep = steps[nextSelectedIndex];
  return {
    draft: { ...state.draft, steps },
    selected:
      state.selected.kind === 'step' && state.selected.clientId === clientId && nextStep
        ? { kind: 'step', clientId: nextStep.clientId }
        : resolveSelection({ ...state.draft, steps }, state.selected),
  };
}

export function moveTreatmentCourseEditorStep(
  state: TreatmentCourseEditorState,
  fromIndex: number,
  toIndex: number,
): TreatmentCourseEditorState {
  return {
    ...state,
    draft: {
      ...state.draft,
      steps: moveTemplateStep(state.draft.steps, fromIndex, toIndex),
    },
  };
}

export function updateTreatmentCourseEditorStep(
  state: TreatmentCourseEditorState,
  clientId: string,
  next: TreatmentCourseTemplateStepDraft,
): TreatmentCourseEditorState {
  return updateTreatmentCourseEditorDraft(state, {
    ...state.draft,
    steps: state.draft.steps.map((step) => (step.clientId === clientId ? next : step)),
  });
}

export function selectedStepIndex(state: TreatmentCourseEditorState): number {
  const selected = state.selected;
  if (selected.kind !== 'step') return -1;
  return state.draft.steps.findIndex((step) => step.clientId === selected.clientId);
}

export function stepHasValidationError(step: TreatmentCourseTemplateStepDraft): boolean {
  return !step.service_id;
}

export function formatIntervalUnitLabel(unit: IntervalUnit, count: number, t: TFunction): string {
  switch (unit) {
    case 'days':
      return t('treatment_courses.interval_unit_days', { count });
    case 'weeks':
      return t('treatment_courses.interval_unit_weeks', { count });
    default: {
      const _exhaustive: never = unit;
      return _exhaustive;
    }
  }
}

export function formatStepIntervalSummary(
  step: TreatmentCourseTemplateStepDraft,
  index: number,
  t: TFunction,
): string | null {
  if (index === 0) return null;
  const unit = formatIntervalUnitLabel(step.interval_unit, step.interval_value, t);
  if (step.interval_tolerance_value > 0) {
    return t('treatment_courses.interval_summary', {
      count: step.interval_value,
      unit,
      tolerance: step.interval_tolerance_value,
      toleranceUnit: formatIntervalUnitLabel(
        step.interval_tolerance_unit,
        step.interval_tolerance_value,
        t,
      ),
    });
  }
  return t('treatment_courses.interval_summary_plain', {
    count: step.interval_value,
    unit,
  });
}

export function formatStepDurationSummary(
  step: TreatmentCourseTemplateStepDraft,
  serviceMinutes: number | undefined,
  t: TFunction,
): string {
  const minutes = step.duration_minutes ?? serviceMinutes;
  if (minutes == null) return t('treatment_courses.duration_inherit_generic');
  return t('treatment_courses.duration_minutes', { minutes });
}

function resolveSelection(
  draft: TreatmentCourseTemplateDraft,
  selected: TreatmentCourseEditorSelection,
): TreatmentCourseEditorSelection {
  if (selected.kind === 'general') return { kind: 'general' };
  const exists = draft.steps.some((step) => step.clientId === selected.clientId);
  if (exists) return selected;
  const first = draft.steps[0];
  return first ? { kind: 'step', clientId: first.clientId } : { kind: 'general' };
}
